import React, { useCallback, useMemo } from 'react';
import { Box, Button, Grid } from '../../../ui';
import { useMCEPageContext } from '../context/MCEPageContext';
import { LayoutBox } from '../../../components';
import { v4 as uuid } from 'uuid';
import { currencyFormatter } from '../../QuotePage/content/QuoteGrids/utils';
import { usePolicyPageContext } from '../../PolicyPage/context/PolicyPageContext';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../hooks';
import { TaxBasisFeeNameCell, TaxBasisNoteProvider, useTaxBasisNote } from '../../../components/TaxBasisNote/TaxBasisNote';

export const MCEFees = React.memo((props, ref) => {
  const { setGridReference, onSelectionChanged, gridReference } = props;
  const { MCEDetails, isEditing, setMCEDetails, isAdjusting, loadedMCE } = useMCEPageContext();
  const { policyId, MCEId } = useParams();
  const { getMceTaxBasis } = useApi();
  // Reloads whenever the MCE is (re)loaded from the server — after a save, adjustment or reset.
  const taxBasisNote = useTaxBasisNote(() => getMceTaxBasis(policyId, MCEId), policyId && MCEId ? loadedMCE : null);
  const { policy, MCEsData } = usePolicyPageContext();
  const isLegacyMCE = MCEDetails?.type === 'legacy';
  const overallPremiumOverwrite = MCEDetails?.overwrites?.find((overwrite) => overwrite?.type === 'overallPremium');

  const setOverallPremiumOverwrite = useCallback(
    (enabled, value) => {
      setMCEDetails((prev) => {
        const overwrites = prev?.overwrites ? [...prev.overwrites] : [];
        const remaining = overwrites.filter((overwrite) => overwrite?.type !== 'overallPremium');
        if (enabled) {
          const entry = { type: 'overallPremium' };
          if (value !== undefined) {
            entry.value = value;
          }
          return { ...prev, overwrites: [...remaining, entry] };
        }
        return { ...prev, overwrites: remaining };
      });
    },
    [setMCEDetails]
  );

  const calculateAmount = (rateOfNet, totalGrossPremium) => {
    return rateOfNet ? (Number(rateOfNet) / 100) * Number(totalGrossPremium) : 0;
  };
  const formatPercentage = (value) => {
    if (value === 0 || isNaN(value)) {
      return 'N/A';
    }
    const formattedValue = Number(value).toFixed(4);
    // Check if the formatted value is '0.00' but the actual value is non-zero
    if (formattedValue === '0.00' && value > 0) {
      return '< 0.01%';
    }
    return `${formattedValue}%`;
  };

  const setAdjustmentValue = useCallback(
    (params) => {
      const updatedValue = params.newValue;
      setMCEDetails((prev) => {
        let adjustment = { ...prev.adjustment };
        adjustment.amount = updatedValue;

        return { ...prev, adjustment: adjustment };
      });
    },
    [setMCEDetails]
  );

  const setGridValues = useCallback(
    (params, MCEDetails, totalGrossPremium) => {
      const { data, newValue } = params;
      const { id } = data;
      const allFees = MCEDetails.fees;
      const editedFieldMap = {
        fee: 'name',
        annual: 'amount',
        percentageOfPremium: 'rateOfNet',
      };
      const editedField = editedFieldMap[params.colDef.field] || '';
      const rowIndex = allFees.findIndex((fee) => fee.id === id);
      const value = params.colDef.dataType === 'number' ? parseFloat(newValue) : newValue;
      if (rowIndex === -1) return;
      allFees[rowIndex][editedField] = isNaN(value) && editedField !== 'name' ? 0 : value;
      const updatedFee = { ...allFees[rowIndex] };
      if (editedField === 'amount') {
        updatedFee.rateOfNet = null;
      } else if (editedField === 'rateOfNet') {
        const newAmount = !isNaN(value) ? Number(calculateAmount(value, totalGrossPremium).toFixed(2)) : updatedFee.amount;
        if (value && !isNaN(value)) {
          updatedFee.amount = newAmount;
        }
      } else if (updatedFee[editedField]) updatedFee[editedField] = value;
      setMCEDetails((prev) => {
        const updatedFees = [...prev.fees];
        updatedFees[rowIndex] = updatedFee;
        return { ...prev, fees: updatedFees };
      });
    },
    [setMCEDetails]
  );

  const calculateOverridePremium = useCallback(() => {
    const policyPremium = Number(policy?.netPremium || 0);
    const mcePremiumSum = (MCEsData || [])
      .filter((mce) => mce?.status === 'executed' || mce?.status === 'released')
      .reduce((sum, mce) => sum + Number(mce?.netPremium || 0), 0);
    const total = Number((-0.1 * (policyPremium + mcePremiumSum)).toFixed(2));
    setMCEDetails((prev) => {
      const updatedFees = prev.fees.map((fee) => {
        if (fee.rateOfNet) {
          return {
            ...fee,
            amount: calculateAmount(fee.rateOfNet, total),
          };
        }
        return fee;
      });
      return {
        ...prev,
        netPremium: total,
        fees: updatedFees,
      };
    });
    setOverallPremiumOverwrite(true, total);
  }, [policy, MCEsData, setMCEDetails, setOverallPremiumOverwrite]);

  const calculateOverridePremium75 = useCallback(() => {
    const policyPremium = Number(policy?.netPremium || 0);
    const mcePremiumSum = (MCEsData || [])
      .filter((mce) => mce?.status === 'executed' || mce?.status === 'released')
      .reduce((sum, mce) => sum + Number(mce?.netPremium || 0), 0);
    const total = Number((-0.25 * (policyPremium + mcePremiumSum)).toFixed(2));
    setMCEDetails((prev) => {
      const updatedFees = prev.fees.map((fee) => {
        if (fee.rateOfNet) {
          return {
            ...fee,
            amount: calculateAmount(fee.rateOfNet, total),
          };
        }
        return fee;
      });
      return {
        ...prev,
        netPremium: total,
        fees: updatedFees,
      };
    });
    setOverallPremiumOverwrite(true, total);
  }, [policy, MCEsData, setMCEDetails, setOverallPremiumOverwrite]);

  const setOverridePremiumValue = useCallback(
    (params) => {
      const enabled = !!params.newValue;
      params.data.overridePremium = enabled;
      setOverallPremiumOverwrite(enabled);
      if (!enabled) {
        setMCEDetails((prev) => {
          const overwrites = prev?.overwrites ? [...prev.overwrites] : [];
          const remaining = overwrites.filter((overwrite) => overwrite?.type !== 'overallPremium');
          return { ...prev, overwrites: remaining };
        });
      }
      return true;
    },
    [setOverallPremiumOverwrite, setMCEDetails]
  );

  const setIncludeLocationSchedule = useCallback(
    (includeLocationSchedule) => {
      setMCEDetails((prev) => {
        const overwrites = prev?.overwrites ? [...prev.overwrites] : [];
        const remaining = overwrites.filter((overwrite) => overwrite?.type !== 'overallPremium');
        const entry = {
          type: 'overallPremium',
          includeLocationSchedule,
        };
        if (overallPremiumOverwrite?.value !== undefined) {
          entry.value = overallPremiumOverwrite.value;
        }
        return { ...prev, overwrites: [...remaining, entry] };
      });
    },
    [setMCEDetails, overallPremiumOverwrite]
  );

  const colDefs = useMemo(() => {
    return [
      {
        headerName: 'Summary of Costs',
        field: 'fee',
        width: 250,
        editable: (params) => params.data.isEditable && isEditing,
        cellClassRules: {
          'ag-cell-editable': (params) => params.data.isEditable && isEditing && params.data.name !== 'Premium',
        },
        valueSetter: (params) => {
          setGridValues(params, MCEDetails);
          return true;
        },
        minWidth: 300,
        cellRenderer: TaxBasisFeeNameCell,
        cellRendererParams: { suppressCount: true },
        headerCheckboxSelection: isEditing,
        checkboxSelection: (params) => params.data?.isEditable && isEditing,
      },
      {
        headerName: 'Percentage of Premium',
        field: 'percentageOfPremium',
        cellClassRules: {
          'ag-cell-editable': (params) => params.data.isEditable && isEditing,
        },
        editable: (params) => {
          if (isLegacyMCE && params.data.fee === 'Premium') return false;
          return params.data.isEditable && isEditing && params.data.fee !== 'Premium';
        },
        valueFormatter: (params) => (params.data ? formatPercentage(params.data.percentageOfPremium) : ''),
        valueSetter: (params) => {
          const oldValue = params.data[params.colDef.field];
          if (oldValue === params.newValue) return false;
          params.api.flashCells({
            rowNodes: [params.node],
            columns: ['annual', 'percentageOfPremium'],
          });
          setGridValues(params, MCEDetails, MCEDetails.netPremium);
          return true;
        },
      },
      {
        headerName: 'MCE Costs',
        field: 'annual',
        editable: (params) => {
          if (isLegacyMCE && isEditing && params.data.fee === 'Premium') return true;
          if (isEditing && params.data.fee === 'Premium') {
            return !!params.data.overridePremium;
          }
          return params.data.isEditable && isEditing;
        },
        valueFormatter: (params) => (params.value ? currencyFormatter({ field: { value: params.value }, withDecimals: true }) : 'N/A'),
        cellClassRules: {
          'ag-cell-editable': (params) => {
            if (isLegacyMCE && isEditing && params.data.fee === 'Premium') return true;
            if (isEditing && params.data.fee === 'Premium') return !!params.data.overridePremium;
            return params.data.isEditable && isEditing && params.data.fee !== 'Premium';
          },
        },
        valueSetter: (params) => {
          const oldValue = params.data[params.colDef.field];
          if (oldValue === params.newValue) return false;

          if (isLegacyMCE && params.data.fee === 'Premium') {
            const newPremium = Number(params.newValue) || 0;
            setMCEDetails((prev) => {
              const updatedFees = prev.fees.map((fee) => {
                if (fee.rateOfNet) {
                  return {
                    ...fee,
                    amount: calculateAmount(fee.rateOfNet, newPremium),
                  };
                }
                return fee;
              });
              return {
                ...prev,
                netPremium: newPremium,
                fees: updatedFees,
              };
            });
            return true;
          }

          if (params.data.fee === 'Premium') {
            const newPremium = Number(params.newValue) || 0;
            setMCEDetails((prev) => ({
              ...prev,
              netPremium: newPremium,
              fees: prev.fees.map((fee) => {
                if (fee.rateOfNet) {
                  return {
                    ...fee,
                    amount: calculateAmount(fee.rateOfNet, newPremium),
                  };
                }
                return fee;
              }),
            }));
            if (params.data.overridePremium) {
              setOverallPremiumOverwrite(true, newPremium);
            }
            return true;
          }

          params.api.flashCells({
            rowNodes: [params.node],
            columns: ['annual', 'percentageOfPremium'],
          });
          setGridValues(params, MCEDetails, MCEDetails.netPremium);
          return true;
        },
      },
      ...(isAdjusting || !!MCEDetails?.adjustment?.fees?.length
        ? [
            {
              headerName: 'Adjustment',
              field: 'adjustment',
              editable: (params) => params.data.isEditable,
              valueFormatter: (params) => (params.value ? currencyFormatter({ field: { value: params.value }, withDecimals: true }) : 'N/A'),
              cellClassRules: {
                'ag-cell-editable': (params) => params.data.isEditable,
              },
              valueSetter: (params) => {
                const oldValue = params.data[params.colDef.field];
                if (oldValue === params.newValue) return false;
                params.api.flashCells({
                  rowNodes: [params.node],
                  columns: ['annual', 'percentageOfPremium', 'adjustment'],
                });
                setAdjustmentValue(params);
                return true;
              },
            },
            {
              headerName: 'MCE Incl Adjustment',
              field: 'mceIncludedAdjustments',
              minWidth: 150,
              valueFormatter: (params) => (params.value ? currencyFormatter({ field: { value: params.value }, withDecimals: true }) : 'N/A'),
            },
          ]
        : []),
      {
        headerName: 'Override',
        field: 'overridePremium',
        minWidth: 120,
        cellRenderer: 'agCheckboxCellRenderer',
        editable: (params) => params.data?.fee === 'Premium' && isEditing && !isLegacyMCE,
        cellClassRules: {
          'ag-cell-editable': (params) => params.data?.fee === 'Premium' && isEditing && !isLegacyMCE,
        },
        valueSetter: setOverridePremiumValue,
      },
    ];
  }, [
    isEditing,
    MCEDetails,
    setGridValues,
    isAdjusting,
    isLegacyMCE,
    setOverallPremiumOverwrite,
    setAdjustmentValue,
    setMCEDetails,
    calculateOverridePremium,
    calculateOverridePremium75,
    setOverridePremiumValue,
    setIncludeLocationSchedule,
  ]);

  const getAdjustmentValue = useCallback(
    (fee) => {
      if (!MCEDetails?.adjustment) return 0;
      const { adjustment: { fees = [], amount } = {} } = MCEDetails || {};
      if (fee.name === 'Premium') return amount;
      const feeAdjustment = fees?.find(({ name }) => name === fee.name);
      return feeAdjustment?.amount || 0;
    },
    [MCEDetails]
  );

  const rowData = useMemo(() => {
    const feeRows = MCEDetails?.fees
      ? MCEDetails.fees.map((fee) => ({
          fee: fee.name,
          percentageOfPremium: fee?.rateOfNet || 0,
          annual: Number(fee.amount) || 0,
          overridePremium: false,
          isEditable: !isAdjusting && MCEDetails.status !== 'executed',
          adjustment: getAdjustmentValue(fee),
          mceIncludedAdjustments: (Number(fee.amount) || 0) + getAdjustmentValue(fee),
          id: fee.id,
          taxBasisRow: { name: fee.name, rate: fee.rateOfNet, amount: fee.amount, isMunicipal: fee.isMunicipal ?? fee.is_municipal },
        }))
      : [];

    const totalCost = MCEDetails?.netPremium + feeRows.reduce((sum, row) => sum + (Number(row.annual) || 0), 0);
    const totalPremium = feeRows.reduce((sum, row) => sum + (Number(row.percentageOfPremium) || 0), 0);
    const totalAdjustment = (Number(MCEDetails?.adjustment?.amount) || 0) + feeRows.reduce((sum, row) => sum + (Number(row.adjustment) || 0), 0);

    return [
      {
        fee: 'Premium',
        percentageOfPremium: 100,
        annual: MCEDetails?.netPremium,
        overridePremium: !!overallPremiumOverwrite,
        isEditable: isAdjusting,
        adjustment: getAdjustmentValue({ name: 'Premium' }),
        mceIncludedAdjustments: MCEDetails?.netPremium + getAdjustmentValue({ name: 'Premium' }),
      },
      ...feeRows,
      {
        fee: 'Total',
        percentageOfPremium: totalPremium,
        annual: totalCost,
        isEditable: false,
        adjustment: totalAdjustment,
        mceIncludedAdjustments: totalCost + totalAdjustment,
      },
    ];
  }, [MCEDetails, isAdjusting, getAdjustmentValue, overallPremiumOverwrite]);

  const handlePress = () => {
    const newRow = {
      id: uuid(),
      name: 'New Fee',
      amount: 0,
      rateOfNet: 0,
    };
    setMCEDetails((prev) => {
      const updatedFees = [...prev.fees, newRow];
      return { ...prev, fees: updatedFees };
    });
    gridReference.api.applyTransaction({ add: [newRow] });
  };

  const onCellValueChanged = (params) => {
    params.api.flashCells({
      rowNodes: [params.node],
      columns: [params.column],
    });
  };

  return (
    <div className="grid-container" style={{ marginBottom: '10px', width: '100%' }}>
      <TaxBasisNoteProvider note={taxBasisNote}>
        <Grid
          data={rowData}
          columns={colDefs}
          floatingFilter={false}
          setGridReference={setGridReference}
          onSelectionChanged={onSelectionChanged}
          shouldShowPagination={false}
          undoRedoCellEditing={true}
          undoRedoCellEditingLimit={100}
          onCellValueChanged={onCellValueChanged}
          rowSelection="multiple"
        />
      </TaxBasisNoteProvider>
      {isEditing && (
        <LayoutBox row justifyContent="space-between" width="full" backgroundColor="white">
          <Box display="flex" alignItems="start" justifyContent="center" flexWrap="wrap" height={70}>
            <Box border="1px solid #919EAB52" marginLeft="$2" borderRadius={8}>
              <Button color="#000" variant="text" onPress={handlePress}>
                Add Fees
              </Button>
            </Box>
            <Box border="1px solid #919EAB52" marginLeft="$2" borderRadius={8}>
              <Button color="#000" variant="text" onPress={calculateOverridePremium} disabled={!overallPremiumOverwrite || isLegacyMCE || !isEditing}>
                Calculate 90% Minimum Earned
              </Button>
            </Box>
            <Box border="1px solid #919EAB52" marginLeft="$2" borderRadius={8}>
              <Button
                color="#000"
                variant="text"
                onPress={calculateOverridePremium75}
                disabled={!overallPremiumOverwrite || isLegacyMCE || !isEditing}
              >
                Calculate 75% Minimum Earned
              </Button>
            </Box>
            {overallPremiumOverwrite && !isLegacyMCE && (
              <Box border="1px solid #919EAB52" marginLeft="$2" borderRadius={8} display="flex" alignItems="center" paddingX={8}>
                <label style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
                  <input
                    type="radio"
                    name="mceSchedule"
                    checked={overallPremiumOverwrite?.includeLocationSchedule === true}
                    onChange={() => setIncludeLocationSchedule(true)}
                  />
                  <span style={{ marginLeft: 6 }}>Include Location Schedule</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="radio"
                    name="mceSchedule"
                    checked={!overallPremiumOverwrite?.includeLocationSchedule}
                    onChange={() => setIncludeLocationSchedule(false)}
                  />
                  <span style={{ marginLeft: 6 }}>Without Location Schedule</span>
                </label>
              </Box>
            )}
          </Box>
        </LayoutBox>
      )}
    </div>
  );
});
